import React, { useState } from 'react';
import {
  IconButton,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import CustomTable from '../General/CustomTable';
import { formatShortDate } from './constants';
import { ReduxState } from '../../types';
import { UserRole, UserType } from '../../constants/user_types';

interface BiweeklyReport {
  id: string;
  startDate: string;
  endDate: string;
  createdBy: string;
  createdAt: string;
}

interface BiweeklyReportTableProps {
  reports: BiweeklyReport[];
  headers: any[];
  loading: boolean;
  onView: (report: BiweeklyReport) => void;
  onDelete?: (report: BiweeklyReport) => void;
}

const BiweeklyReportTable: React.FC<BiweeklyReportTableProps> = ({
  reports,
  headers,
  loading,
  onView,
  onDelete,
}) => {
  const currentUser = useSelector((state: ReduxState) => state.account.user);
  const isAdmin = currentUser?.role === UserRole.ADMIN && currentUser?.type === UserType.CDTEC;
  const isAgro = currentUser?.role === UserRole.AGRO && currentUser?.type === UserType.CDTEC;

  const canDelete = isAdmin || isAgro;
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<BiweeklyReport | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (report: BiweeklyReport) => {
    setReportToDelete(report);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete || !onDelete) return;
    setIsDeleting(true);
    await onDelete(reportToDelete);
    setIsDeleting(false);
    setDeleteConfirmOpen(false);
    setReportToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setReportToDelete(null);
  };

  const reportData = {
    count: reports.length,
    values: reports.map((report) => ({
      id: report.id,
      createdAt: formatShortDate(report.createdAt),
      createdBy: report.createdBy,
      startDate: formatShortDate(report.startDate),
      endDate: formatShortDate(report.endDate),
      actions: (
        <Box display="flex" gap={1}>
          <IconButton onClick={() => onView(report)} size="small">
            <VisibilityIcon />
          </IconButton>
          {canDelete && onDelete && (
            <IconButton onClick={() => handleDeleteClick(report)} size="small">
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    })),
  };

  return (
    <Box>
      {loading ? (
        <CircularProgress size={'3rem'} />
      ) : (
        <CustomTable headers={headers} rows={reportData} />
      )}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Confirmar eliminación
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea eliminar este informe quincenal?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="outlined" disabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BiweeklyReportTable;
